/**
 * 7-step Product Master Form. Reused by sellers (route: /seller/products/new|/:id/edit)
 * and admins. Pure presentational; the parent page handles wrapping + page chrome.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { api } from "../../lib/api";
import { toast } from "sonner";
import ImageUploader from "../../components/common/ImageUploader";
import TryOnPanel from "../../components/common/TryOnPanel";
import { Check, ChevronLeft, ChevronRight, Eye, Plus, Trash2 } from "lucide-react";
import { hexForColorName, isValidHex } from "../../lib/colorHex";

const STEPS = [
  { key: "basic", label: "Basic" },
  { key: "attrs", label: "Attributes" },
  { key: "variants", label: "Variants" },
  { key: "media", label: "Media" },
  { key: "shipping", label: "Shipping & Returns" },
  { key: "seo", label: "SEO" },
  { key: "review", label: "Review" },
];

const slugify = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

const defaults = () => ({
  title: "", short_description: "", description: "", brand: "",
  department_id: "", category_id: "", subcategory_id: "", section_id: "",
  attributes: {}, color: "", fabric: "", occasion: "",
  price: 0, mrp: 0, sizes: [], variants: [],
  images: [], video_url: "",
  package: { length_cm: "", width_cm: "", height_cm: "", weight_g: "" },
  return_allowed: true, return_window_days: 7, cod_allowed: true, is_ready_to_ship: false,
  slug: "", meta_title: "", meta_description: "", keywords: [],
  status: "draft",
});

function StepNav({ step, setStep, valid }) {
  return (
    <ol className="flex flex-wrap gap-2 mb-8" data-testid="step-nav">
      {STEPS.map((s, i) => {
        const active = i === step;
        const done = i < step;
        return (
          <li key={s.key}>
            <button
              type="button"
              onClick={() => setStep(i)}
              disabled={!valid && i > step}
              className={`px-3 py-2 text-xs uppercase tracking-wider border ${active ? "bg-[hsl(var(--kb-primary))] text-white border-[hsl(var(--kb-primary))]" : done ? "border-[hsl(var(--kb-primary))]/40 text-[hsl(var(--kb-primary))]" : "border-[hsl(var(--kb-border))] text-[hsl(var(--kb-muted))]"}`}
              data-testid={`step-${s.key}`}
            >
              {done && <Check size={12} className="inline mr-1" />}
              {i + 1}. {s.label}
            </button>
          </li>
        );
      })}
    </ol>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="overline block mb-1">{label}</label>
      {children}
      {hint && <p className="text-[11px] text-[hsl(var(--kb-muted))] mt-1">{hint}</p>}
    </div>
  );
}

export default function ProductWizard() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdminScope = location.pathname.startsWith("/admin/");
  const scopeBase = isAdminScope ? "/admin/products" : "/seller/products";
  const isEdit = !!id;

  const [step, setStep] = useState(0);
  const [form, setForm] = useState(defaults());
  const [tree, setTree] = useState([]);
  const [attrSchema, setAttrSchema] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setAttr = (k, v) => setForm((f) => ({ ...f, attributes: { ...f.attributes, [k]: v } }));
  const setPkg = (k, v) => setForm((f) => ({ ...f, package: { ...f.package, [k]: v } }));

  // ---------- Data load ----------
  useEffect(() => {
    let alive = true;
    api.get("/categories/tree")
      .then(({ data }) => { if (alive) setTree(data || []); })
      .catch(() => {});
    if (isEdit) {
      setLoading(true);
      // Authenticated edit fetch — the public /products/{slug} applies the
      // storefront visibility predicate, so a draft or deactivated product
      // 404s there and the wizard could not reopen it.
      api.get(`/products/manage/${id}`)
        .then(({ data }) => {
          if (!alive) return;
          // Strip server-only fields
          const { seller, category, rating_avg, review_count, created_at, updated_at, approval, _id, ...rest } = data;
          // Coerce nulls to empty strings on controlled fields so React doesn't warn
          const NULLABLE_STR = ["department_id", "category_id", "subcategory_id", "section_id", "brand", "short_description", "video_url", "slug", "meta_title", "meta_description", "color", "fabric", "occasion"];
          NULLABLE_STR.forEach((k) => { if (rest[k] == null) rest[k] = ""; });
          setForm({ ...defaults(), ...rest, package: { ...defaults().package, ...(rest.package || {}) }, keywords: rest.keywords || [] });
        })
        .catch((e) => setError(e?.response?.data?.detail || "Failed to load product"))
        .finally(() => { if (alive) setLoading(false); });
    }
    return () => { alive = false; };
  }, [id, isEdit]);

  // ---------- Hierarchy helpers ----------
  const departments = tree;
  const categories = useMemo(() => {
    const dept = departments.find((d) => d.id === form.department_id);
    return dept?.children || [];
  }, [departments, form.department_id]);
  const subcategories = useMemo(() => {
    const cat = categories.find((c) => c.id === form.category_id);
    return cat?.children || [];
  }, [categories, form.category_id]);
  const sections = useMemo(() => {
    const sub = subcategories.find((s) => s.id === form.subcategory_id);
    return sub?.children || [];
  }, [subcategories, form.subcategory_id]);

  // ---------- Dynamic attribute schema for the deepest selected level ----------
  const deepestSlug = useMemo(() => {
    // Resolve from the dependent selectors first (creation flow)
    const sec = sections.find((s) => s.id === form.section_id);
    if (sec) return sec.slug;
    const sub = subcategories.find((s) => s.id === form.subcategory_id);
    if (sub) return sub.slug;
    const cat = categories.find((c) => c.id === form.category_id);
    if (cat) return cat.slug;
    // Edit-mode fallback — walk the whole tree (department_id may be absent on legacy products)
    const target = form.section_id || form.subcategory_id || form.category_id;
    if (!target) return null;
    const walk = (nodes) => {
      for (const n of nodes || []) {
        if (n.id === target) return n.slug;
        const found = walk(n.children);
        if (found) return found;
      }
      return null;
    };
    return walk(tree);
  }, [sections, subcategories, categories, form, tree]);

  useEffect(() => {
    if (!deepestSlug) { setAttrSchema([]); return; }
    let alive = true;
    api.get("/catalog/attributes", { params: { category_slug: deepestSlug } })
      .then(({ data }) => { if (alive) setAttrSchema(data?.attributes || []); })
      .catch(() => { if (alive) setAttrSchema([]); });
    return () => { alive = false; };
  }, [deepestSlug]);

  // ---------- Auto slug on title change (only when empty / when creating) ----------
  useEffect(() => {
    if (!form.title) return;
    setForm((f) => ({ ...f, slug: f.slug || slugify(f.title) }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.title]);

  // ---------- Validation per step ----------
  const errors = useMemo(() => {
    const e = {};
    if (!form.title?.trim()) e.title = "Title required";
    if (!form.category_id) e.category_id = "Pick department & category";
    const price = Number(form.price);
    const mrp = Number(form.mrp);
    if (!Number.isFinite(price) || price <= 0) e.price = "Selling price > 0";
    if (Number.isFinite(mrp) && mrp > 0 && mrp < price) e.mrp = "MRP must be ≥ selling price";
    return e;
  }, [form]);

  const stepValid = useMemo(() => {
    if (step === 0) return !errors.title && !errors.category_id;
    if (step === 2) return !errors.price && !errors.mrp;
    return true;
  }, [step, errors]);

  // ---------- Variant matrix ----------
  const addVariant = () => set("variants", [...(form.variants || []), {
    sku: "", barcode: "", color: form.color || "",
    color_hex: hexForColorName(form.color) || "",
    size: "",
    mrp: form.mrp || "", price: form.price || "", stock: 0,
    weight_g: "", length_cm: "", width_cm: "", height_cm: "", images: [],
  }]);
  const updVariant = (idx, k, v) => {
    const arr = [...form.variants];
    arr[idx] = { ...arr[idx], [k]: v };
    // Auto-seed a swatch hex when the admin renames a colour to a
    // common name — but ONLY when the field is empty so we never
    // clobber a manually-picked hex.
    if (k === "color" && !arr[idx].color_hex) {
      const guess = hexForColorName(v);
      if (guess) arr[idx].color_hex = guess;
    }
    // Reverse direction: when the admin picks a hex swatch and hasn't
    // typed a colour name yet, seed the name with the hex string so
    // the storefront/cart have a stable identity for this variant.
    // Without this, two "swatch-only" variants collide into a single
    // empty-string cart line.
    if (k === "color_hex" && isValidHex(v) && !(arr[idx].color || "").trim()) {
      arr[idx].color = v;
    }
    set("variants", arr);
  };
  const delVariant = (idx) => set("variants", form.variants.filter((_, i) => i !== idx));

  const moveImage = (from, to) => {
    if (to < 0 || to >= form.images.length) return;
    const arr = [...form.images];
    const [m] = arr.splice(from, 1);
    arr.splice(to, 0, m);
    set("images", arr);
  };

  // Drag-and-drop image reorder
  const [dragIdx, setDragIdx] = useState(null);
  const onDragStart = (i) => () => setDragIdx(i);
  const onDragOver = (i) => (e) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === i) return;
    moveImage(dragIdx, i);
    setDragIdx(i);
  };
  const onDragEnd = () => setDragIdx(null);

  // Variant matrix auto-builder (Step 3 helper)
  const [matrix, setMatrix] = useState({ colors: "", sizes: "", baseStock: 0 });
  const generateMatrix = () => {
    const colors = matrix.colors.split(",").map((s) => s.trim()).filter(Boolean);
    const sizes = matrix.sizes.split(",").map((s) => s.trim()).filter(Boolean);
    if (!colors.length && !sizes.length) {
      toast.error("Enter at least one color or size");
      return;
    }
    const existing = new Set((form.variants || []).map((v) => `${(v.color || "").toLowerCase()}::${(v.size || "").toLowerCase()}`));
    const rows = [];
    let skipped = 0;
    const colorList = colors.length ? colors : [form.color || ""];
    const sizeList = sizes.length ? sizes : [""];
    colorList.forEach((color) => {
      sizeList.forEach((s) => {
        const key = `${color.toLowerCase()}::${s.toLowerCase()}`;
        if (existing.has(key)) { skipped += 1; return; }
        existing.add(key);
        rows.push({
          sku: "", barcode: "", color, color_hex: hexForColorName(color) || "",
          size: s,
          mrp: form.mrp || "", price: form.price || "", stock: Number(matrix.baseStock) || 0,
          weight_g: "", length_cm: "", width_cm: "", height_cm: "", images: [],
        });
      });
    });
    if (rows.length === 0) {
      toast.error("All combinations already exist");
      return;
    }
    set("variants", [...(form.variants || []), ...rows]);
    setMatrix({ colors: "", sizes: "", baseStock: 0 });
    const msg = skipped > 0
      ? `Generated ${rows.length} variant${rows.length === 1 ? "" : "s"} (skipped ${skipped} duplicate${skipped === 1 ? "" : "s"})`
      : `Generated ${rows.length} variant${rows.length === 1 ? "" : "s"}`;
    toast.success(msg);
  };

  // ---------- Submit ----------
  const buildPayload = (status) => {
    const variants = (form.variants || []).map((v) => ({
      ...v,
      // Only persist color_hex when it's a valid #rrggbb; drop
      // garbage strings so backend validation never rejects the
      // whole product save.
      color_hex: isValidHex(v.color_hex) ? v.color_hex : null,
      mrp: v.mrp === "" || v.mrp == null ? null : Number(v.mrp),
      price: v.price === "" || v.price == null ? null : Number(v.price),
      stock: Number(v.stock) || 0,
      weight_g: v.weight_g === "" ? null : Number(v.weight_g),
      length_cm: v.length_cm === "" ? null : Number(v.length_cm),
      width_cm: v.width_cm === "" ? null : Number(v.width_cm),
      height_cm: v.height_cm === "" ? null : Number(v.height_cm),
    }));
    const pkg = Object.fromEntries(Object.entries(form.package || {}).map(([k, v]) => [k, v === "" ? null : Number(v)]));
    return {
      ...form,
      status,
      price: Number(form.price) || 0,
      mrp: Number(form.mrp) || 0,
      return_window_days: Number(form.return_window_days) || 0,
      keywords: Array.isArray(form.keywords) ? form.keywords : (form.keywords || "").split(",").map((s) => s.trim()).filter(Boolean),
      variants,
      package: pkg,
    };
  };

  const save = async (status) => {
    if (Object.keys(errors).length) {
      toast.error(Object.values(errors)[0]);
      return;
    }
    setSaving(true);
    try {
      const body = buildPayload(status);
      const { data } = isEdit
        ? await api.put(`/products/${id}`, body)
        : await api.post("/products", body);
      toast.success(status === "draft" ? "Saved as draft" : "Submitted for approval");
      navigate(isEdit ? scopeBase : `${scopeBase}/${data.id}/edit`, { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  // ---------- Render ----------
  if (loading) return <div className="p-8 text-[hsl(var(--kb-muted))]" data-testid="wizard-loading">Loading product…</div>;
  if (error) return <div className="p-8 text-red-700" data-testid="wizard-error">{error}</div>;

  return (
    <div className="max-w-5xl" data-testid="product-wizard">
      <div className="overline mb-2">{isEdit ? "Edit" : "New"}</div>
      <h1 className="font-display text-3xl md:text-4xl mb-6">{isEdit ? form.title || "Edit Product" : "Add Product"}</h1>

      <StepNav step={step} setStep={setStep} valid={stepValid} />

      <div className="bg-white border border-[hsl(var(--kb-border))] p-6 space-y-6" data-testid={`step-pane-${STEPS[step].key}`}>
        {/* STEP 1 — BASIC */}
        {step === 0 && (
          <div className="space-y-5">
            <Field label="Product Title">
              <input className="kb-input" value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="wiz-title" />
            </Field>
            <Field label="Short Description" hint="One-liner shown on product cards (≤140 chars)">
              <input maxLength={140} className="kb-input" value={form.short_description || ""} onChange={(e) => set("short_description", e.target.value)} data-testid="wiz-short-desc" />
            </Field>
            <Field label="Full Description">
              <textarea rows={5} className="kb-input" value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="wiz-desc" />
            </Field>
            <Field label="Brand / Designer">
              <input className="kb-input" value={form.brand || ""} onChange={(e) => set("brand", e.target.value)} data-testid="wiz-brand" />
            </Field>
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Department">
                <select className="kb-input" value={form.department_id} onChange={(e) => setForm((f) => ({ ...f, department_id: e.target.value, category_id: "", subcategory_id: "", section_id: "" }))} data-testid="wiz-dept">
                  <option value="">Select…</option>
                  {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </Field>
              <Field label="Category">
                <select className="kb-input" value={form.category_id} onChange={(e) => setForm((f) => ({ ...f, category_id: e.target.value, subcategory_id: "", section_id: "" }))} disabled={!categories.length} data-testid="wiz-cat">
                  <option value="">{categories.length ? "Select…" : "Pick department first"}</option>
                  {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </Field>
              <Field label="Subcategory">
                <select className="kb-input" value={form.subcategory_id || ""} onChange={(e) => setForm((f) => ({ ...f, subcategory_id: e.target.value, section_id: "" }))} disabled={!subcategories.length} data-testid="wiz-sub">
                  <option value="">{subcategories.length ? "Optional…" : "—"}</option>
                  {subcategories.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </Field>
              <Field label="Section / Collection">
                <select className="kb-input" value={form.section_id || ""} onChange={(e) => set("section_id", e.target.value)} disabled={!sections.length} data-testid="wiz-section">
                  <option value="">{sections.length ? "Optional…" : "—"}</option>
                  {sections.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </Field>
            </div>
          </div>
        )}

        {/* STEP 2 — DYNAMIC ATTRS */}
        {step === 1 && (
          <div className="space-y-5">
            <p className="text-sm text-[hsl(var(--kb-muted))]">
              Attributes loaded from <b>{deepestSlug || "selected category"}</b>. {attrSchema.length === 0 && "No dynamic attributes configured for this category — admin can add them in the Attributes panel."}
            </p>
            {(() => {
              const dynamicSlugs = new Set(attrSchema.map((a) => a.slug));
              return (
                <div className="grid md:grid-cols-2 gap-4">
                  {/* Built-in three only when not in dynamic schema */}
                  {!dynamicSlugs.has("color") && (
                    <Field label="Color"><input className="kb-input" value={form.color || ""} onChange={(e) => set("color", e.target.value)} data-testid="wiz-color" /></Field>
                  )}
                  {!dynamicSlugs.has("fabric") && (
                    <Field label="Fabric"><input className="kb-input" value={form.fabric || ""} onChange={(e) => set("fabric", e.target.value)} data-testid="wiz-fabric" /></Field>
                  )}
                  {!dynamicSlugs.has("occasion") && (
                    <Field label="Occasion"><input className="kb-input" value={form.occasion || ""} onChange={(e) => set("occasion", e.target.value)} data-testid="wiz-occasion" /></Field>
                  )}

                  {attrSchema.map((a) => {
                    // Bind built-in fields directly so storefront filters keep working
                    const isBuiltin = ["color", "fabric", "occasion"].includes(a.slug);
                    const value = isBuiltin ? (form[a.slug] || "") : (form.attributes?.[a.slug] ?? "");
                    const onChange = (val) => isBuiltin ? set(a.slug, val) : setAttr(a.slug, val);
                    return (
                      <Field key={a.slug} label={a.name}>
                        {a.type === "boolean" ? (
                          <label className="flex items-center gap-2 text-sm pt-2">
                            <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} data-testid={`wiz-attr-${a.slug}`} />
                            Yes
                          </label>
                        ) : a.values?.length ? (
                          <select className="kb-input" value={value} onChange={(e) => onChange(e.target.value)} data-testid={`wiz-attr-${a.slug}`}>
                            <option value="">—</option>
                            {a.values.map((v) => <option key={v.slug} value={v.value}>{v.label || v.value}</option>)}
                          </select>
                        ) : (
                          <input className="kb-input" value={value} onChange={(e) => onChange(e.target.value)} data-testid={`wiz-attr-${a.slug}`} />
                        )}
                      </Field>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        )}

        {/* STEP 3 — VARIANTS */}
        {step === 2 && (
          <div className="space-y-5">
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="MRP (₹)">
                <input type="number" min="0" className="kb-input" value={form.mrp} onChange={(e) => set("mrp", e.target.value)} data-testid="wiz-mrp" />
              </Field>
              <Field label="Selling price (₹)">
                <input type="number" min="0" className="kb-input" value={form.price} onChange={(e) => set("price", e.target.value)} data-testid="wiz-price" />
                {errors.price && <span className="text-xs text-red-700">{errors.price}</span>}
                {errors.mrp && <span className="text-xs text-red-700 ml-2">{errors.mrp}</span>}
              </Field>
            </div>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="overline">Variants ({form.variants.length})</div>
              <button type="button" onClick={addVariant} className="kb-btn-outline !py-1 !px-3 !text-xs flex items-center gap-1" data-testid="wiz-add-variant">
                <Plus size={12} /> Add Variant
              </button>
            </div>

            <div className="border border-dashed border-[hsl(var(--kb-border))] p-3 grid md:grid-cols-4 gap-2 items-end" data-testid="matrix-builder">
              <div>
                <label className="overline block mb-1 text-[10px]">Colors (comma)</label>
                <input className="kb-input !text-xs" placeholder="Red, Blue, Green" value={matrix.colors} onChange={(e) => setMatrix({ ...matrix, colors: e.target.value })} data-testid="matrix-colors" />
              </div>
              <div>
                <label className="overline block mb-1 text-[10px]">Sizes (comma)</label>
                <input className="kb-input !text-xs" placeholder="S, M, L, XL" value={matrix.sizes} onChange={(e) => setMatrix({ ...matrix, sizes: e.target.value })} data-testid="matrix-sizes" />
              </div>
              <div>
                <label className="overline block mb-1 text-[10px]">Stock per variant</label>
                <input type="number" min="0" className="kb-input !text-xs" value={matrix.baseStock} onChange={(e) => setMatrix({ ...matrix, baseStock: e.target.value })} data-testid="matrix-stock" />
              </div>
              <button type="button" onClick={generateMatrix} className="kb-btn-primary !py-2 !text-xs" data-testid="matrix-generate">
                Generate Matrix
              </button>
            </div>
            {form.variants.length === 0 ? (
              <div className="text-sm text-[hsl(var(--kb-muted))] border border-dashed border-[hsl(var(--kb-border))] p-6 text-center">
                Add variants to manage SKU-level inventory. Leave empty to use the simple price + sizes mode.
              </div>
            ) : (
              <div className="overflow-x-auto -mx-6 md:mx-0">
                <table className="min-w-full text-xs">
                  <thead className="bg-[hsl(var(--kb-surface-alt))]">
                    <tr>
                      <th className="p-2 text-left">Color</th>
                      <th className="p-2 text-left">Swatch</th>
                      <th className="p-2 text-left">Size</th>
                      <th className="p-2 text-left">SKU</th>
                      <th className="p-2 text-left">Barcode</th>
                      <th className="p-2 text-left">MRP</th>
                      <th className="p-2 text-left">Price</th>
                      <th className="p-2 text-left">Stock</th>
                      <th className="p-2 text-left">Wt(g)</th>
                      <th className="p-2 text-left">L×W×H cm</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.variants.map((v, i) => (
                      <tr key={i} className="border-t border-[hsl(var(--kb-border))]" data-testid={`wiz-variant-${i}`}>
                        <td className="p-1"><input className="kb-input !py-1 !px-2 !text-xs" value={v.color || ""} onChange={(e) => updVariant(i, "color", e.target.value)} data-testid={`wiz-variant-color-${i}`} /></td>
                        <td className="p-1" data-testid={`wiz-variant-swatch-cell-${i}`}>
                          <div className="flex items-center gap-1">
                            <input
                              type="color"
                              value={isValidHex(v.color_hex) ? v.color_hex : "#cccccc"}
                              onChange={(e) => updVariant(i, "color_hex", e.target.value)}
                              className="w-7 h-7 rounded border border-[hsl(var(--kb-border))] bg-transparent cursor-pointer p-0"
                              title="Pick swatch colour"
                              data-testid={`wiz-variant-hex-picker-${i}`}
                              aria-label="Swatch colour picker"
                            />
                            <input
                              type="text"
                              value={v.color_hex || ""}
                              onChange={(e) => updVariant(i, "color_hex", e.target.value)}
                              className="kb-input !py-1 !px-2 !text-[10px] font-mono w-20"
                              placeholder="#RRGGBB"
                              maxLength={7}
                              data-testid={`wiz-variant-hex-input-${i}`}
                            />
                          </div>
                        </td>
                        <td className="p-1"><input className="kb-input !py-1 !px-2 !text-xs w-16" value={v.size || ""} onChange={(e) => updVariant(i, "size", e.target.value)} data-testid={`wiz-variant-size-${i}`} /></td>
                        <td className="p-1"><input className="kb-input !py-1 !px-2 !text-xs font-mono" placeholder="auto" value={v.sku || ""} onChange={(e) => updVariant(i, "sku", e.target.value)} /></td>
                        <td className="p-1"><input className="kb-input !py-1 !px-2 !text-xs font-mono" value={v.barcode || ""} onChange={(e) => updVariant(i, "barcode", e.target.value)} /></td>
                        <td className="p-1"><input type="number" className="kb-input !py-1 !px-2 !text-xs w-20" value={v.mrp ?? ""} onChange={(e) => updVariant(i, "mrp", e.target.value)} /></td>
                        <td className="p-1"><input type="number" className="kb-input !py-1 !px-2 !text-xs w-20" value={v.price ?? ""} onChange={(e) => updVariant(i, "price", e.target.value)} /></td>
                        <td className="p-1"><input type="number" min="0" className="kb-input !py-1 !px-2 !text-xs w-16" value={v.stock ?? 0} onChange={(e) => updVariant(i, "stock", e.target.value)} /></td>
                        <td className="p-1"><input type="number" className="kb-input !py-1 !px-2 !text-xs w-16" value={v.weight_g ?? ""} onChange={(e) => updVariant(i, "weight_g", e.target.value)} /></td>
                        <td className="p-1">
                          <div className="flex gap-1">
                            <input type="number" className="kb-input !py-1 !px-1 !text-xs w-12" placeholder="L" value={v.length_cm ?? ""} onChange={(e) => updVariant(i, "length_cm", e.target.value)} />
                            <input type="number" className="kb-input !py-1 !px-1 !text-xs w-12" placeholder="W" value={v.width_cm ?? ""} onChange={(e) => updVariant(i, "width_cm", e.target.value)} />
                            <input type="number" className="kb-input !py-1 !px-1 !text-xs w-12" placeholder="H" value={v.height_cm ?? ""} onChange={(e) => updVariant(i, "height_cm", e.target.value)} />
                          </div>
                        </td>
                        <td className="p-1"><button type="button" onClick={() => delVariant(i)} className="text-red-700" data-testid={`wiz-variant-del-${i}`}><Trash2 size={14} /></button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <p className="text-xs text-[hsl(var(--kb-muted))]">SKU left blank will be auto-generated as <code>SLUG-COLOR-SIZE-NN</code> on save. The swatch column pins the colour shown on the storefront — leave blank to fall back to a name-based guess.</p>
          </div>
        )}

        {/* STEP 4 — MEDIA */}
        {step === 3 && (
          <div className="space-y-5">
            <div>
              <label className="overline block mb-2">Gallery Images</label>
              {/* No `cropAspect` prop → cropping is opt-in per image via the
                  Crop button on each thumbnail (see below). Multi-file
                  selection is restored (Feb 2026 fix — the previous default
                  forced square crop AND single-file selection). */}
              <ImageUploader value={form.images} onChange={(arr) => set("images", arr)} max={12} testid="wiz-images" />
              {form.images.length > 0 && (
                <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mt-3" data-testid="wiz-image-grid">
                  {form.images.map((src, i) => (
                    <div
                      key={`${src}-${i}`}
                      draggable
                      onDragStart={onDragStart(i)}
                      onDragOver={onDragOver(i)}
                      onDragEnd={onDragEnd}
                      onDrop={onDragEnd}
                      className={`relative group border ${dragIdx === i ? "border-[hsl(var(--kb-primary))] opacity-50" : "border-[hsl(var(--kb-border))]"} cursor-move transition-opacity`}
                      data-testid={`wiz-image-${i}`}
                    >
                      <img src={src.startsWith("http") ? src : `${process.env.REACT_APP_BACKEND_URL}${src}`} alt="" className="w-full aspect-[3/4] object-contain bg-[hsl(var(--kb-surface-alt))] pointer-events-none" />
                      {i === 0 && <span className="absolute top-1 left-1 bg-[hsl(var(--kb-primary))] text-white text-[9px] px-1.5 py-0.5 uppercase tracking-wider">Main</span>}
                      <div className="absolute bottom-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button type="button" onClick={() => moveImage(i, i - 1)} className="bg-white/90 px-1 py-0.5 text-[10px]" disabled={i === 0} data-testid={`wiz-img-up-${i}`}>↑</button>
                        <button type="button" onClick={() => moveImage(i, i + 1)} className="bg-white/90 px-1 py-0.5 text-[10px]" disabled={i === form.images.length - 1} data-testid={`wiz-img-down-${i}`}>↓</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {form.images.length > 0 && (
                <p className="text-[10px] text-[hsl(var(--kb-muted))] mt-2">Drag to reorder · first image is the main thumbnail</p>
              )}
            </div>
            <Field label="Video URL" hint="YouTube / Vimeo / direct mp4">
              <input className="kb-input" value={form.video_url || ""} onChange={(e) => set("video_url", e.target.value)} placeholder="https://…" data-testid="wiz-video" />
            </Field>

            <div className="pt-2">
              <TryOnPanel
                productId={id || form.id}
                sourceImages={form.images}
                onSave={(updatedImages) => set("images", updatedImages)}
              />
            </div>
          </div>
        )}

        {/* STEP 5 — SHIPPING & RETURNS */}
        {step === 4 && (
          <div className="space-y-5">
            <div className="grid md:grid-cols-4 gap-3">
              <Field label="Weight (g)"><input type="number" min="0" className="kb-input" value={form.package?.weight_g ?? ""} onChange={(e) => setPkg("weight_g", e.target.value)} data-testid="wiz-weight" /></Field>
              <Field label="Length (cm)"><input type="number" min="0" className="kb-input" value={form.package?.length_cm ?? ""} onChange={(e) => setPkg("length_cm", e.target.value)} /></Field>
              <Field label="Width (cm)"><input type="number" min="0" className="kb-input" value={form.package?.width_cm ?? ""} onChange={(e) => setPkg("width_cm", e.target.value)} /></Field>
              <Field label="Height (cm)"><input type="number" min="0" className="kb-input" value={form.package?.height_cm ?? ""} onChange={(e) => setPkg("height_cm", e.target.value)} /></Field>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.return_allowed} onChange={(e) => set("return_allowed", e.target.checked)} data-testid="wiz-return-allowed" /> Allow returns
              </label>
              <Field label="Return window (days)">
                <input type="number" min="0" className="kb-input" value={form.return_window_days} onChange={(e) => set("return_window_days", e.target.value)} disabled={!form.return_allowed} data-testid="wiz-return-days" />
              </Field>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.cod_allowed} onChange={(e) => set("cod_allowed", e.target.checked)} data-testid="wiz-cod" /> Cash on delivery
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.is_ready_to_ship} onChange={(e) => set("is_ready_to_ship", e.target.checked)} data-testid="wiz-rts" /> Ready to ship
              </label>
            </div>
          </div>
        )}

        {/* STEP 6 — SEO */}
        {step === 5 && (
          <div className="space-y-5">
            <Field label="URL Slug" hint="Auto-generated from title — edit only if you have a reason">
              <input className="kb-input font-mono" value={form.slug || ""} onChange={(e) => set("slug", slugify(e.target.value))} data-testid="wiz-slug" />
            </Field>
            <Field label="Meta Title" hint="≤60 chars (Google preview)">
              <input maxLength={70} className="kb-input" value={form.meta_title || ""} onChange={(e) => set("meta_title", e.target.value)} data-testid="wiz-meta-title" />
            </Field>
            <Field label="Meta Description" hint="≤160 chars">
              <textarea rows={3} maxLength={180} className="kb-input" value={form.meta_description || ""} onChange={(e) => set("meta_description", e.target.value)} data-testid="wiz-meta-desc" />
            </Field>
            <Field label="Keywords (comma-separated)">
              <input
                className="kb-input"
                value={Array.isArray(form.keywords) ? form.keywords.join(", ") : (form.keywords || "")}
                onChange={(e) => set("keywords", e.target.value)}
                onBlur={(e) => set("keywords", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
                data-testid="wiz-keywords"
              />
            </Field>
          </div>
        )}

        {/* STEP 7 — REVIEW */}
        {step === 6 && (
          <div className="space-y-5">
            <div className="grid md:grid-cols-2 gap-3 text-sm">
              <div><b>Title:</b> {form.title || <span className="text-red-700">missing</span>}</div>
              <div><b>Slug:</b> <span className="font-mono text-xs">{form.slug || "(auto)"}</span></div>
              <div><b>Brand:</b> {form.brand || "—"}</div>
              <div><b>Category:</b> {deepestSlug || <span className="text-red-700">missing</span>}</div>
              <div><b>Price:</b> ₹{form.price || 0} <span className="text-[hsl(var(--kb-muted))]">/ MRP ₹{form.mrp || 0}</span></div>
              <div><b>Variants:</b> {form.variants.length}</div>
              <div><b>Images:</b> {form.images.length}</div>
              <div><b>RTS:</b> {form.is_ready_to_ship ? "yes" : "no"} · <b>COD:</b> {form.cod_allowed ? "yes" : "no"} · <b>Returns:</b> {form.return_allowed ? `${form.return_window_days}d` : "no"}</div>
            </div>
            <div className="border border-[hsl(var(--kb-border))] p-4 text-xs">
              <div className="overline mb-2">Validation</div>
              {Object.keys(errors).length === 0 ? (
                <span className="text-green-700">All required fields look good.</span>
              ) : (
                <ul className="text-red-700 list-disc pl-5 space-y-1">
                  {Object.values(errors).map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              )}
            </div>
            <div className="flex flex-wrap gap-3">
              <button type="button" className="kb-btn-outline" onClick={() => save("draft")} disabled={saving} data-testid="wiz-save-draft">
                {saving ? "Saving…" : "Save as Draft"}
              </button>
              <button type="button" className="kb-btn-primary" onClick={() => save("active")} disabled={saving || Object.keys(errors).length > 0} data-testid="wiz-submit">
                {saving ? "Submitting…" : "Submit for Approval"}
              </button>
              {isEdit && form.slug && (
                <Link to={`/product/${form.slug}`} target="_blank" rel="noreferrer" className="kb-btn-outline flex items-center gap-2" data-testid="wiz-preview">
                  <Eye size={14} /> Preview Product Page
                </Link>
              )}
            </div>
          </div>
        )}

        {/* STEP NAVIGATION */}
        <div className="flex justify-between pt-6 border-t border-[hsl(var(--kb-border))]">
          <button
            type="button"
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            disabled={step === 0}
            className="kb-btn-outline flex items-center gap-2"
            data-testid="wiz-prev"
          >
            <ChevronLeft size={14} /> Back
          </button>
          {step < STEPS.length - 1 ? (
            <button
              type="button"
              onClick={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))}
              disabled={!stepValid}
              className="kb-btn-primary flex items-center gap-2"
              data-testid="wiz-next"
            >
              Next <ChevronRight size={14} />
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
